// BulkUpload.ts
import type {
  PayrollReviewTransactionViewSummaryResponse,
  GxwpsBulkFileTransaction,
  PaymentsBulkFileTransaction,
  PaymentsBulkReviewTransViewSummaryResponse,
  PaymentsBulkApprovalViewSummaryResponse,
  PayrollBulkApprovalViewSummaryResponse,
} from "@/app/types/bulkPaymentsPayroll";
import type { GridRowClickEvent } from "@progress/kendo-react-grid";

export interface BulkUpload {
  bulkUploadId: number;
  gxChnNo: string;
  fileName?: string;
  crtdDt: string;
  crtdName?: string;
  totalTxn: number;
  validTxn: number;
  invalidTxn: number;
  fileAmount: number;
  currency?: string;
  recordStatusCode?: string;
  recordStatusCodeDesc?: string;
  noOfApprLevel?: number;
  authzHists?: AuthzHist[];
  payerMetromapDto?: any;
  payerName?: string;
  payerEmailId?: string;
  payerMaskEmailId?: string;
  payerMobileNo?: string;
  payerMobileCode?: string;
  payerMaskMobileNo?: string;
  payerAddressLine1?: string;
  payerCountryName?: string;
}

export interface AuthzHist {
  authzLevel: number;
  aprdStatus: string;
  aprdStatusDesc?: string;
  aprdName?: string;
  aprdDt?: string;
  remarks?: string;
}

export interface BulkUploadListPayload {
  status: string;
  pageNo: number;
  pageSize: number;
  sortBy: string;
  searchText?: string;
}

export interface BulkUploadApprovePayload {
  bulkUploadId: number | number[];
  aprdStatus: "A" | "R";
  authzType: "BULK_UPLOAD";
  remarks?: string;
}

export interface BulkUploadApproveResponse {
  status: number;
  data: {
    bulkUploadId?: number;
    aprdStatus: "A" | "R";
    message?: string;
    successCount?: number;
    totalCount?: number;
    failedUploads?: Array<{
      bulkUploadId: number;
      reason: string;
    }>;
  };
}

export interface UploadFormData {
  depositTo?: string;
  accountDepositTo?: string;
  depositAccountNumber?: string;
  debitCurrency?: string;
}

export interface BulkPaymentReviewCardListProps {
  allTransactions: PaymentsBulkFileTransaction[];
  validTransactions: PaymentsBulkFileTransaction[];
  invalidTransactions: PaymentsBulkFileTransaction[];

  // CB-only: pending transactions (status 4506) for the Pending tab
  pendingTransactions?: PaymentsBulkFileTransaction[];
  allPayrollTransactions: GxwpsBulkFileTransaction[];
  validPayrollTransactions: GxwpsBulkFileTransaction[];
  invalidPayrollTransactions: GxwpsBulkFileTransaction[];
  bulkType: string;
  reviewSummary: PaymentsBulkReviewTransViewSummaryResponse | null;
  payrollReviewSummary: PayrollReviewTransactionViewSummaryResponse | null;
  isPayroll: boolean;
  shouldShowTabs: boolean;
  shouldShowTransactionSummary: boolean;
  loading?: boolean;
  fileType?: string;
  onTabChange?: (tab: string) => Promise<void>;
  tabLoading?: boolean;
}

// ============================================================================
// BULK UPLOAD TYPES
// ============================================================================

export interface FileUploadSuccessData {
  fileName: string;
  gxJobProcessid: string;
  gxReferenceNo: string;
}

export interface FormErrors {
  accessStartDate: string;
  bulkType: string;
  fileFormat: string;
  debitAccount: string;
  uploadedFile: string;
}

export interface FormValues {
  accessStartDate: string;
}

// ============================================================================
// BULK UPLOAD SUCCESS SCREEN TYPES
// ============================================================================
export interface FileUploadSuccessProps {
  fileName: string;
  gxJobProcessid: string;
  gxReferenceNo: string;
  onClose: () => void;
  showProcessSuccess?: boolean;
}

// ============================================================================
// MANAGE BULK FILE PAGE SCREEN TYPES
// ============================================================================

/* ----------------------------------
 * Constants
 * ----------------------------------*/
export const PAGE_SIZE = 10;

/* ----------------------------------
 * Types
 * ----------------------------------*/
export interface BulkFileRow {
  growxRefNo: string;
  uploadedOn: string;
  totalTxn: number | "--";
  debitAmount: number | "--";
  validTxn: number | "--";
  invalidTxn: number | "--";
  pendingTxn: number | "--";
  status: string;
  currency?: string;
  jobStatusDesc?: string;
  gxJobProcessid: number;
  gxReferenceNo: string;
  gxJobChnNo: string;
  rejectNotes?: string;
  fileFormat?: string | null;
}

export interface StatusOption {
  label: string;
  value: string;
}

// ============================================================================
// BulkUploadView PAGE SCREEN TYPES
// ============================================================================
export interface BulkUploadViewProps {
  paymentData?: PaymentsBulkFileTransaction | null;
  payrollData?: GxwpsBulkFileTransaction | null;
  reviewSummary?: PaymentsBulkReviewTransViewSummaryResponse | null;
  payrollReviewSummary?: PayrollReviewTransactionViewSummaryResponse | null;
  isPayroll?: boolean;
  usePayrollApi?: boolean;    // <-- ADD new
  transactionIdentifiers?: {
    uniqueKey: number;
    statusId: string;
  } | null;
}

// ============================================================================
// BulkPaymentReviewOverview PAGE SCREEN TYPES
// ============================================================================

export interface BulkPaymentReviewOverviewProps {
  reviewSummary: PaymentsBulkReviewTransViewSummaryResponse | null;
  bulkType: string;
  transferredData?: any;
  payrollReviewSummary?: PayrollReviewTransactionViewSummaryResponse | null;
  isPayroll?: boolean;
  shouldShowTransactionSummary?: boolean;
  logoName?: string;
  logoPath?: string;
  bankName?: string;
  debitAccountNo?: string;
}

// ============================================================================
// BulkUploadIndividualDetails PAGE SCREEN TYPES
// ============================================================================

export const STATUS_CODES = {
  UPLOAD_SUCCESS: "4500",
  VALIDATION_IN_PROGRESS: "4502",
  VALIDATION_FAILED: "4505",
  READY_FOR_REVIEW_PAYMENT: "4510",
  AWAITING_APPROVAL: "4515",
  IN_PROCESS: "4550",
  FUTURE_DATED: "4580",
  REJECTED: "4585",
  PROCESSED: "4590",
  READY_FOR_REVIEW_PAYROLL: "7510",
} as const;

export interface BulkUploadIndividualDetailsProps {
  bulkData?:
    | PaymentsBulkApprovalViewSummaryResponse
    | PayrollBulkApprovalViewSummaryResponse;
  bulkType?: string;
  isApprovalMode?: boolean;

  // lets parent capture the detail-API summary (has real approval counts)
  onSummaryLoaded?: (
    summary:
      | PaymentsBulkReviewTransViewSummaryResponse
      | PayrollReviewTransactionViewSummaryResponse,
  ) => void;
}

// ============================================================================
// BulkUploadApprovePayroll PAGE SCREEN TYPES
// ============================================================================
export interface Access {
  view: boolean;
  approve: boolean;
}

export interface BulkUploadApprovePayrollProp {
  setIsOpenBulkListPayroll: (value: boolean) => void;
  isOpenBulkListPayroll: boolean;
  access: Access;
  onTotalCountUpdate?: (count: number) => void;
  setCallCount: React.Dispatch<React.SetStateAction<boolean>>;
}

// ============================================================================
// BulkUploadApprove PAGE SCREEN TYPES
// ============================================================================

export interface Access {
  view: boolean;
  approve: boolean;
}

export interface BulkUploadApproveProp {
  setIsOpenBulkList: (value: boolean) => void;
  isOpenBulkList: boolean;
  access: Access;
  setCallCount: React.Dispatch<React.SetStateAction<boolean>>;

  // row-click detail-only entry point (no list dialog opened)
  initialSelectedBeneficiary?: BulkUploadIndividualDetailsProps | null;
  initialSelectedBeneficiaryKey?: number;
}

// ============================================================================
// BulkUploadApprove PAGE SCREEN TYPES
// ============================================================================
export interface BulkUploadApproveTableProps {
  onRowClick: (event: GridRowClickEvent) => void;
  onViewDetails?: (record: PaymentsBulkApprovalViewSummaryResponse) => void;
  showAll?: boolean;
  bulkUploads: PaymentsBulkApprovalViewSummaryResponse[];
  selectedBulkUploads?: number[];
  setSelectedBulkUploads?: React.Dispatch<React.SetStateAction<number[]>>;
  setCheckAll?: React.Dispatch<React.SetStateAction<boolean>>;
  checkAll?: boolean;
  approveAccess: boolean;
  maxSelectionLimit?: number;
  isLoading: boolean;
  hideCbCheckbox?: boolean; // hide checkbox when CB routing
}

// ============================================================================
// BulkUploadApproveTablePayroll PAGE SCREEN TYPES
// ============================================================================
export interface BulkUploadApproveTablePayrollProps {
  onRowClick: (event: GridRowClickEvent) => void;
  showAll?: boolean;
  selectedBulkUploads?: number[];
  setSelectedBulkUploads?: React.Dispatch<React.SetStateAction<number[]>>;
  setCheckAll?: React.Dispatch<React.SetStateAction<boolean>>;
  checkAll?: boolean;
  approveAccess: boolean;
  bulkUploads: PayrollBulkApprovalViewSummaryResponse[];
  isLoading: boolean;
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  maxSelectionLimit?: number;
  hideCbCheckbox?: boolean; // hide checkbox when CB routing
}
